/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useId, useRef, useState } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap, jsx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import TextArea from '@atlaskit/textarea/text-area';
import { token } from '@atlaskit/tokens';

import type {
	InferredProjectFeedbackReason,
	InferredProjectFeedbackSubmission,
} from '../entity-annotations/types';
import messages from './inferredProjectCardMessages';

const FEEDBACK_REASONS: readonly InferredProjectFeedbackReason[] = [
	'notAProject',
	'wrongTitleOrDescription',
	'unrelatedSources',
	'missingSources',
	'wrongContributors',
	'alreadyExists',
	'other',
];

const REASON_MESSAGES: Record<InferredProjectFeedbackReason, MessageDescriptor> = {
	notAProject: messages.reasonNotAProject,
	wrongTitleOrDescription: messages.reasonWrongTitleOrDescription,
	unrelatedSources: messages.reasonUnrelatedSources,
	missingSources: messages.reasonMissingSources,
	wrongContributors: messages.reasonWrongContributors,
	alreadyExists: messages.reasonAlreadyExists,
	other: messages.reasonOther,
};

const styles = cssMap({
	form: {
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
		// A programmatic focus target that is not in the tab order, so it shows no focus ring.
		'&:focus': {
			outline: 'none',
		},
		'&:focus-visible': {
			outline: 'none',
		},
	},
	// `Heading` has no subtle colour, which the design uses for this title.
	title: {
		color: token('color.text.subtle'),
		font: token('font.heading.xxsmall'),
	},
});

export type InferredProjectFeedbackReasonsFormProps = {
	onClose: () => void;
	onSubmit: (submission: InferredProjectFeedbackSubmission) => void;
};

export const InferredProjectFeedbackReasonsForm = ({
	onClose,
	onSubmit,
}: InferredProjectFeedbackReasonsFormProps): React.ReactNode => {
	const { formatMessage } = useIntl();
	const headingId = useId();
	const additionalFeedbackLabelId = useId();
	const formRef = useRef<HTMLDivElement>(null);
	const [selectedReasons, setSelectedReasons] = useState<
		ReadonlySet<InferredProjectFeedbackReason>
	>(() => new Set());
	const [additionalFeedback, setAdditionalFeedback] = useState('');

	// Moves focus off the thumb that opened the form, so screen readers announce the form.
	useEffect(() => {
		formRef.current?.focus();
	}, []);

	const toggleReason = (reason: InferredProjectFeedbackReason) => {
		setSelectedReasons((previous) => {
			const next = new Set(previous);
			if (next.has(reason)) {
				next.delete(reason);
			} else {
				next.add(reason);
			}
			return next;
		});
	};

	const handleSubmit = () => {
		const trimmedFeedback = additionalFeedback.trim();
		onSubmit({
			reasons: FEEDBACK_REASONS.filter((reason) => selectedReasons.has(reason)),
			...(trimmedFeedback ? { additionalFeedback: trimmedFeedback } : {}),
		});
	};

	return (
		<Box
			ref={formRef}
			aria-labelledby={headingId}
			role="group"
			tabIndex={-1}
			testId="inferred-project-card-feedback-reasons"
			xcss={styles.form}
		>
			<Stack space="space.100">
				<Inline alignBlock="start" space="space.100" spread="space-between">
					<Box as="h3" id={headingId} xcss={styles.title}>
						{formatMessage(messages.feedbackReasonsHeading)}
					</Box>
					<IconButton
						appearance="subtle"
						icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
						isTooltipDisabled={false}
						label={formatMessage(messages.closeFeedbackReasons)}
						onClick={onClose}
						shape="circle"
						spacing="compact"
					/>
				</Inline>
				<Inline shouldWrap space="space.100">
					{FEEDBACK_REASONS.map((reason) => {
						const isSelected = selectedReasons.has(reason);
						return (
							<Button
								key={reason}
								aria-pressed={isSelected}
								isSelected={isSelected}
								onClick={() => toggleReason(reason)}
							>
								{formatMessage(REASON_MESSAGES[reason])}
							</Button>
						);
					})}
				</Inline>
				<Stack space="space.050">
					<Text
						color="color.text.subtlest"
						id={additionalFeedbackLabelId}
						size="small"
						weight="bold"
					>
						{formatMessage(messages.additionalFeedbackLabel)}
					</Text>
					<TextArea
						aria-labelledby={additionalFeedbackLabelId}
						onChange={(event) => setAdditionalFeedback(event.target.value)}
						placeholder={formatMessage(messages.additionalFeedbackPlaceholder)}
						testId="inferred-project-card-feedback-additional"
						value={additionalFeedback}
					/>
				</Stack>
				<Button
					appearance="primary"
					isDisabled={selectedReasons.size === 0}
					onClick={handleSubmit}
					shouldFitContainer
				>
					{formatMessage(messages.submitFeedbackReasons)}
				</Button>
			</Stack>
		</Box>
	);
};
